import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { CorrespondenceAttachment, CorrespondenceFiles, LocalFile } from '../../../domain/models/correspondence.model';

/**
 * Inline images + attachments for a template or a send. Existing files come from the
 * server (with links); new ones stay in the browser until the parent saves/sends.
 * The parent reads {@link value} and previews the body with {@link cidUrls}.
 */
@Component({
  selector: 'app-correspondence-files',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule],
  template: `
    <div class="files">
      <div class="files-row">
        <span class="files-label">Images in the message</span>
        <button mat-stroked-button type="button" (click)="imgInput.click()">
          <mat-icon>add_photo_alternate</mat-icon> Add image
        </button>
        <input #imgInput type="file" accept="image/*" hidden multiple (change)="pickInline($event)">
      </div>
      <div class="chips" *ngIf="existingInline.length || newInline.length; else noImages">
        <span class="chip" *ngFor="let f of existingInline">
          <img [src]="f.url" alt="" class="thumb">
          <span class="chip-name" [title]="f.fileName">{{ f.fileName }}</span>
          <button mat-icon-button type="button" title="Insert into message" (click)="insertImage.emit(f.contentId!)"><mat-icon>code</mat-icon></button>
          <button mat-icon-button type="button" title="Remove" (click)="dropExisting(f)"><mat-icon>close</mat-icon></button>
        </span>
        <span class="chip" *ngFor="let f of newInline">
          <img [src]="f.previewUrl" alt="" class="thumb">
          <span class="chip-name" [title]="f.file.name">{{ f.file.name }}</span>
          <button mat-icon-button type="button" title="Insert into message" (click)="insertImage.emit(f.cid!)"><mat-icon>code</mat-icon></button>
          <button mat-icon-button type="button" title="Remove" (click)="dropNew(newInline, f)"><mat-icon>close</mat-icon></button>
        </span>
      </div>
      <ng-template #noImages><div class="muted">No images. Added images are inserted where the cursor is.</div></ng-template>

      <div class="files-row">
        <span class="files-label">Attachments</span>
        <button mat-stroked-button type="button" (click)="fileInput.click()">
          <mat-icon>attach_file</mat-icon> Add attachment
        </button>
        <input #fileInput type="file" hidden multiple (change)="pickAttachment($event)">
      </div>
      <div class="chips" *ngIf="existingPlain.length || newPlain.length; else noFiles">
        <span class="chip" *ngFor="let f of existingPlain">
          <mat-icon class="chip-icon">description</mat-icon>
          <a class="chip-name" [href]="f.url" target="_blank" rel="noopener" [title]="f.fileName">{{ f.fileName }}</a>
          <small class="muted">{{ size(f.fileSize) }}</small>
          <button mat-icon-button type="button" title="Remove" (click)="dropExisting(f)"><mat-icon>close</mat-icon></button>
        </span>
        <span class="chip" *ngFor="let f of newPlain">
          <mat-icon class="chip-icon">description</mat-icon>
          <span class="chip-name" [title]="f.file.name">{{ f.file.name }}</span>
          <small class="muted">{{ size(f.file.size) }}</small>
          <button mat-icon-button type="button" title="Remove" (click)="dropNew(newPlain, f)"><mat-icon>close</mat-icon></button>
        </span>
      </div>
      <ng-template #noFiles><div class="muted">No attachments.</div></ng-template>
      <div class="muted" *ngIf="totalBytes > maxBytes" class="warn">Files total {{ size(totalBytes) }}; e-mails are capped at {{ size(maxBytes) }}.</div>
    </div>
  `,
  styles: [`
    .files { display: flex; flex-direction: column; gap: 10px; }
    .files-row { display: flex; align-items: center; gap: 12px; }
    .files-label { font-size: 13px; font-weight: 600; color: #475569; flex: 1; }
    .chips { display: flex; flex-wrap: wrap; gap: 8px; }
    .chip { display: inline-flex; align-items: center; gap: 6px; border: 1px solid #e2e8f0; border-radius: 8px; padding: 2px 4px 2px 8px; background: #fff; max-width: 100%; }
    .chip-name { font-size: 13px; color: #1e293b; max-width: 220px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .chip-icon { color: #64748b; font-size: 18px; width: 18px; height: 18px; }
    .thumb { width: 32px; height: 32px; object-fit: cover; border-radius: 4px; }
    .muted { font-size: 12px; color: #94a3b8; }
    .warn { color: #b91c1c; }
  `],
})
export class CorrespondenceFilesComponent implements OnDestroy {
  /** Files already on the server (template or batch). */
  @Input() set existing(list: CorrespondenceAttachment[] | null | undefined) {
    this.existingAll = [...(list ?? [])];
    this.removedIds.clear();
  }
  /** Fired with the content id when the user wants `<img src="cid:...">` inserted into the body. */
  @Output() insertImage = new EventEmitter<string>();
  @Output() changed = new EventEmitter<void>();

  readonly maxBytes = 8 * 1024 * 1024;
  @ViewChild('imgInput') private imgInput?: ElementRef<HTMLInputElement>;
  @ViewChild('fileInput') private fileInput?: ElementRef<HTMLInputElement>;

  /** Opened from the editor toolbar. */
  openImagePicker(): void { this.imgInput?.nativeElement.click(); }
  openFilePicker(): void { this.fileInput?.nativeElement.click(); }
  existingAll: CorrespondenceAttachment[] = [];
  removedIds = new Set<number>();
  newInline: LocalFile[] = [];
  newPlain: LocalFile[] = [];

  get existingInline(): CorrespondenceAttachment[] { return this.existingAll.filter((f) => f.inline && !this.removedIds.has(f.id)); }
  get existingPlain(): CorrespondenceAttachment[] { return this.existingAll.filter((f) => !f.inline && !this.removedIds.has(f.id)); }

  get totalBytes(): number {
    return [...this.existingInline, ...this.existingPlain].reduce((s, f) => s + (f.fileSize ?? 0), 0)
      + [...this.newInline, ...this.newPlain].reduce((s, f) => s + f.file.size, 0);
  }

  /** What the parent sends to the server. */
  get value(): CorrespondenceFiles {
    return {
      keepIds: [...this.existingInline, ...this.existingPlain].map((f) => f.id),
      attachments: this.newPlain.map((f) => f.file),
      inline: this.newInline,
    };
  }

  /** cid → URL for previewing the body. */
  get cidUrls(): Record<string, string> {
    const m: Record<string, string> = {};
    this.existingInline.forEach((f) => { if (f.contentId && f.url) m[f.contentId] = f.url; });
    this.newInline.forEach((f) => { if (f.cid) m[f.cid] = f.previewUrl; });
    // Same object while nothing changed, so template bindings don't see a "new" value every check.
    const key = JSON.stringify(m);
    if (key !== this.cidKey) { this.cidKey = key; this.cidCache = m; }
    return this.cidCache;
  }
  private cidKey = '';
  private cidCache: Record<string, string> = {};

  pickInline(ev: Event): void {
    const input = ev.target as HTMLInputElement;
    Array.from(input.files ?? []).forEach((file) => {
      const cid = `img-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      this.newInline.push({ file, cid, previewUrl: URL.createObjectURL(file) });
      this.insertImage.emit(cid);
    });
    input.value = '';
    this.changed.emit();
  }

  pickAttachment(ev: Event): void {
    const input = ev.target as HTMLInputElement;
    Array.from(input.files ?? []).forEach((file) => this.newPlain.push({ file, previewUrl: '' }));
    input.value = '';
    this.changed.emit();
  }

  dropExisting(f: CorrespondenceAttachment): void { this.removedIds.add(f.id); this.changed.emit(); }

  dropNew(list: LocalFile[], f: LocalFile): void {
    const i = list.indexOf(f);
    if (i >= 0) { list.splice(i, 1); if (f.previewUrl) URL.revokeObjectURL(f.previewUrl); }
    this.changed.emit();
  }

  reset(): void {
    [...this.newInline, ...this.newPlain].forEach((f) => f.previewUrl && URL.revokeObjectURL(f.previewUrl));
    this.newInline = []; this.newPlain = []; this.removedIds.clear();
  }

  size(bytes: number | null): string {
    if (!bytes) return '';
    return bytes < 1024 * 1024 ? `${Math.round(bytes / 1024)} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  }

  ngOnDestroy(): void { this.reset(); }
}
